import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import Analytics from '../module/analytics';
import Cookie from 'js-cookie';
import axios from 'axios';
import { delay } from 'bluebird';

import { ReactComponent as IconCheck } from '../assets/Icon-check-green.svg';

interface ContactFormProps {
  show?: boolean;
  title?: string;
  onModalClose?: () => void;
}

interface ContactFormState {
  formData?: any;
  loading?: boolean;
  formSubmittedMessage?: string;
  showSuccessModal?: boolean;
}

const FormBox: React.FC<{ style?: React.CSSProperties; className?: string; id?: string }> = ({
  children,
  style,
  className,
  id,
}) => (
  <div id={id} style={style} className={className}>
    <div tw="bg-white dark:bg-black-300 w-full h-full p-6 rounded-3xl flex flex-col justify-between">
      {children}
    </div>
  </div>
);

const contactUs = {
  form: [
    {
      contentfulid: 'name',
      label: 'Nama Lengkap',
      type: 'Text',
      placeholder: 'Tulis nama Anda',
      column: 1,
      required: true,
    },
    {
      contentfulid: 'companyName',
      label: 'Perusahaan / Institusi',
      type: 'Text',
      placeholder: 'Tulis nama perusahaan',
      column: 1,
      required: false,
    },
    {
      contentfulid: 'phoneNumber',
      label: 'No. HP',
      type: 'Number',
      placeholder: 'Tulis nomor HP',
      column: 2,
      required: true,
    },
    {
      contentfulid: 'email',
      label: 'Email',
      type: 'Email',
      placeholder: 'Tulis Email Anda',
      column: 2,
      required: true,
    },
    {
      contentfulid: 'question',
      label: 'Pesan atau Pertanyaan (opsional)',
      type: 'Textarea',
      placeholder: 'Tulis pesan atau pernyataan Anda (opsional)',
      column: 1,
      required: false,
    },
  ],
  urlText: 'Kirim',
};

class ContactForm extends React.Component<ContactFormProps, ContactFormState> {
  constructor(props: ContactFormProps) {
    super(props);
    this.state = {
      formData: {
        name: '',
        companyName: '',
        phoneNumber: '',
        email: '',
        question: '',
      },
      loading: false,
      formSubmittedMessage: null,
      showSuccessModal: false,
    };
    this.onSubmitForm = this.onSubmitForm.bind(this);
    this.onChangeForm = this.onChangeForm.bind(this);
  }

  getGAClientID(): string {
    if (typeof window !== undefined) {
      return Cookie.get('_ga');
    }
    return null;
  }

  sendFormSubmissionSuccessEvent() {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({
        event: 'formSubmissionSuccess',
        formId: 'pma-contact',
      });
    }
  }

  onChangeForm(event) {
    this.setState({
      formData: { ...this.state.formData, [event.target.name]: event.target.value },
    });
  }

  onSuccessModalClose() {
    this.setState({ showSuccessModal: false });
    this.onComponentClose();
  }

  onComponentClose() {
    this.setState({
      formData: {
        name: '',
        companyName: '',
        phoneNumber: '',
        email: '',
        question: '',
      },
    });
    this.props.onModalClose();
  }

  async onSubmitForm(event) {
    event.preventDefault();
    if (!this.state.formData.email) {
      toast.error('Email cannot be empty', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      return;
    }

    this.setState({ loading: true });
    try {
      const url = `https://easybiz-id.netlify.app/.netlify/functions/report-pma-form`;
      const analyticsData = Analytics();
      const gaClientId = this.getGAClientID();
      const bodyFormData = {
        gaClientId: gaClientId,
        cohort: 'original',
        source: analyticsData.source,
        medium: analyticsData.medium,
        companyName: this.state.formData?.companyName,
        name: this.state.formData?.name,
        userPhoneNumber: this.state.formData?.phoneNumber,
        email: this.state.formData?.email,
        message: this.state.formData?.question,
        email_title: this.props?.title,
        recipients_name: ['Yukiko'],
        recipients_email: ['yukiko@easybiz.id'],
        number_pool: ['6282282226878'],
      };
      let result = await axios({
        method: 'post',
        url,
        data: bodyFormData,
        headers: { 'Content-Type': 'application/json' },
      });
      this.setState({
        formData: {
          name: '',
          companyName: '',
          phoneNumber: '',
          email: '',
          question: '',
        },
      });
      this.sendFormSubmissionSuccessEvent();
      toast.success(result?.data?.message || 'Success Send Email', {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      this.setState({ showSuccessModal: true });
      this.setState({ loading: false });
      this.props.onModalClose();
      return;
    } catch (error) {
      toast.error(error?.message, {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
      this.setState({ formSubmittedMessage: 'Error, please try again later' });
    }

    this.setState({ loading: false });
    await delay(5000);
    this.setState({ formSubmittedMessage: null });
  }

  render() {
    return (
      <>
        <div
          tw="fixed w-full h-full top-0 right-0 left-0 bottom-0"
          css={css`
            ${this.props.show ? tw`block` : tw`hidden`}
            z-index: 10;
            background: rgb(0, 0, 0, 0.4);
          `}
        >
          <div
            tw="absolute w-full h-full cursor-pointer"
            onClick={() => {
              this.props.onModalClose();
            }}
          ></div>
          <div
            tw="relative left-0 lg:left-1/3 top-14 w-full lg:w-2/3"
            css={css`
              min-height: 50%;
              @media (min-width: 1025px) {
                top: 7%;
              }
            `}
          >
            <div tw="relative w-full h-full">
              <FormBox
                tw="w-full lg:w-1/2 pb-4 lg:p-4 relative mb-6 lg:mb-4"
                key={'form-contact'}
                id={'form-contact'}
                css={css`
                  z-index: 19;
                `}
              >
                <div tw="flex justify-between">
                  <div tw="text-b1 font-extrabold my-2 text-black-100 dark:text-white">
                    Hubungi Kami
                  </div>
                  <div
                    tw="text-b1 my-2 text-black-100 dark:text-white cursor-pointer"
                    onClick={() => {
                      this.onComponentClose();
                    }}
                  >
                    x
                  </div>
                </div>
                <div tw="text-b1 my-4 text-black-100 dark:text-white">
                  Tim Sales Easybiz akan menghubungi Anda ketika formulir sudah terkirim dalam 24
                  jam kedepan (hari kerja).
                </div>
                <form onSubmit={this.onSubmitForm} tw="grid grid-cols-6 gap-2">
                  {contactUs?.form?.map((f, i) => {
                    return (
                      <div
                        key={`${f.contentfulid}-${i}-form`}
                        css={css`
                          ${f.column === 1
                            ? tw`col-span-6`
                            : f.column === 2
                            ? tw`col-span-3`
                            : f.column === 3
                            ? tw`col-span-2`
                            : tw`col-span-1`}
                          @media (max-width: 1024px) {
                            ${tw`col-span-6`}
                          }
                        `}
                      >
                        <label tw="w-full text-b2 my-2 lg:text-black-100 dark:text-white lg:dark:text-white">
                          {f.label}
                        </label>
                        <div
                          tw="w-full my-2"
                          css={css`
                            textarea {
                              ${tw`w-full rounded-xl border-black-500 text-b1 p-4`}
                              min-height: 48px;
                              border: 1px solid #dadada;
                              &:focus {
                                ${tw`rounded-xl`}
                              }
                              &:focus-visible {
                                ${tw`outline-none`}
                              }
                            }

                            input {
                              ${tw`w-full rounded-xl border-black-500 inline-block text-b1 px-4 box-border`}
                              min-height: 48px;
                              border: 1px solid #dadada;
                              &:focus {
                                ${tw`rounded-xl`}
                              }
                              &:focus-visible {
                                ${tw`outline-none`}
                              }
                            }
                          `}
                        >
                          {f.type === 'Textarea' ? (
                            <textarea
                              name={f.contentfulid}
                              id={f.contentfulid}
                              cols={30}
                              rows={8}
                              placeholder={f.placeholder}
                              value={this.state?.formData[f.contentfulid]}
                              onChange={this.onChangeForm}
                              required={f.required}
                            ></textarea>
                          ) : (
                            <input
                              name={f.contentfulid}
                              id={f.contentfulid}
                              type={f.type.toLowerCase()}
                              placeholder={f.placeholder}
                              value={this.state?.formData[f.contentfulid]}
                              onChange={this.onChangeForm}
                              required={f.required}
                              inputMode={f.type.toLowerCase() === 'number' ? 'numeric' : 'text'}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {this.state.formSubmittedMessage ? (
                    <div tw="text-left text-b2 text-black-100 dark:text-white flex col-span-6">
                      <div tw="mr-2">
                        <span
                          tw="rounded-full text-white flex items-center justify-center"
                          css={css`
                            font-size: 12px;
                            width: 20px;
                            height: 20px;
                            background-color: #ff0000;
                          `}
                        >
                          {'X'}
                        </span>
                      </div>
                      {this.state.formSubmittedMessage}
                    </div>
                  ) : (
                    <div tw="col-span-6">
                      <div tw="">
                        <input
                          disabled={this.state.loading}
                          type="submit"
                          value={contactUs?.urlText || 'Kirim'}
                          tw="col-span-6 text-center bg-yellow-bright hover:bg-khaki active:bg-golden font-gilroy border-0 text-blue-marine font-semibold py-3 px-16 rounded-full cursor-pointer no-underline inline-block w-full"
                          css={css`
                            ${this.state.loading
                              ? tw`bg-black-500 hover:bg-black-300 active:bg-black-500 cursor-not-allowed`
                              : tw`bg-yellow-bright`}
                          `}
                        ></input>
                      </div>
                      <div
                        tw="mt-4 col-span-6 text-center bg-transparent font-gilroy border-0 text-blue-marine dark:text-white font-semibold cursor-pointer no-underline inline-block w-full"
                        css={css`
                          ${this.state.loading ? tw`cursor-not-allowed` : tw``}
                        `}
                        onClick={() => {
                          this.onComponentClose();
                        }}
                      >
                        Batal
                      </div>
                    </div>
                  )}
                </form>
              </FormBox>
            </div>
          </div>
        </div>
        <div
          tw="fixed w-full h-full top-0 right-0 left-0 bottom-0"
          css={css`
            ${this.state.showSuccessModal ? tw`block` : tw`hidden`}
            z-index: 20;
            background: rgb(0, 0, 0, 0.4);
          `}
        >
          <div
            tw="relative left-0 lg:left-1/3 top-20 w-full lg:w-2/3"
            css={css`
              min-height: 50%;
              @media (min-width: 1025px) {
                top: 20%;
              }
            `}
          >
            <div tw="relative w-full h-full">
              <FormBox
                tw="w-full lg:w-1/2 pb-4 lg:p-4 relative mb-6 lg:mb-4"
                key={'modal-confirm'}
                id={'modal-confirm'}
              >
                <div tw="grid grid-cols-6 gap-2">
                  <div tw="col-span-6 text-center">
                    <IconCheck tw="flex-none"></IconCheck>
                  </div>
                  <div tw="text-b1 my-2 text-black-100 dark:text-white col-span-6">
                    Terima kasih sudah tertarik untuk menggunakan layanan kami! Permintaan Anda akan
                    segera kami proses. Tim Easybiz akan segera menghubungi Anda.
                  </div>
                  <div tw="col-span-6">
                    <div tw="">
                      <button
                        tw="col-span-6 text-center bg-yellow-bright hover:bg-khaki active:bg-golden font-gilroy border-0 text-blue-marine font-semibold py-3 px-16 rounded-full cursor-pointer no-underline inline-block w-full"
                        onClick={() => {
                          this.onSuccessModalClose();
                        }}
                      >
                        Kembali ke Halaman Utama
                      </button>
                    </div>
                    <div
                      tw="mt-4 col-span-6 text-center bg-transparent font-gilroy border-0 text-blue-marine dark:text-white font-semibold cursor-pointer no-underline inline-block w-full"
                      onClick={() => {
                        this.setState({ showSuccessModal: false });
                      }}
                    >
                      Tutup
                    </div>
                  </div>
                </div>
              </FormBox>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default ContactForm;
